import { Injectable } from '@angular/core';
import { EmailTemplatesEnum } from '@interfaces/models/email-templates';
import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { removeRestrictedHoldings } from '@shared/helpers/restricted-holdings';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { flowMapper } from '@shared/models';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import {
  DocumentUploadService,
  EmailService,
  FMFService,
  selectOAFromStore,
  SubmitService,
} from '@shared/services';
import {
  selectTestMonkeyState,
  testMonkeyNextScenario,
  testMonkeyResetState,
  testMonkeyUpdateResponses,
} from '@src/app/test-monkey/store';
import {
  appianSubmit,
  convertRegistration,
  internalTransferAppianSubmit,
  submitCompleted,
  submitFailed,
  submitInProgress,
  updateIsStp,
} from '@store/actions';
import { getClientName } from '@store/reducers/personal-data/personal-data.reducer';
import {
  getIsGiftingFlow,
  getIsInternalTransferFlow,
  getIsIRASingleExecutor,
  getIsMinorFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import {
  selectCO0UploadToggle,
  selectIsOpeningNewAccountAutomatically,
  selectIsProspect,
  selectTriageState,
} from '@store/selectors';
import { selectFraudDetectorSimulatorLevel } from '@store/selectors/fraud-detector-simulator/fraud-detector-simulator.selectors';
import { selectLetterFilesMetadata } from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectAcceptedDAOffer } from '@store/selectors/open-choice/open-choice.selectors';
import { PDFDocument } from 'pdf-lib';
import { catchError, filter, forkJoin, from, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { selectClientPersonalInfoData, selectRecaptchaMockResult } from 'vg-interaction-model';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';

import {
  closeSpinner,
  enrollInDA,
  Go,
  LogError,
  LogInfo,
  Next,
  submitMetricsFlowSubmit,
  TrackProcessComplete,
  TrackProcessError,
  TrackSendDocuments,
  UpdateAccountId,
} from '../../actions';
import * as fromRootStore from '../../index';
import { getOpenAccountState } from '../../reducers';
import { IFileMetaData } from '../../reducers/document-upload/document-upload.reducer';
import { selectFilesMetadata } from '../../selectors/document-upload/document-upload.selectors';
import { selectClientEmailAddress } from '../../selectors/personal-data/personal-data.selectors';
import {
  getITSubmitPayload,
  getSubmitPayload,
  selectIsSubmitInProgress,
} from '../../selectors/submit/submit.selectors';

@Injectable()
export class SubmitEffects {
  constructor(
    private actions$: Actions,
    private submitService: SubmitService,
    private store$: Store<fromRootStore.State>,
    private loggerService: LoggerService,
    private windowRef: WindowRef,
    private emailService: EmailService,
    private fmfService: FMFService,
    private documentUploadService: DocumentUploadService,
  ) {}

  submit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appianSubmit),
      withLatestFrom(
        this.store$.pipe(select(selectIsSubmitInProgress)),
        this.store$.pipe(select(getSubmitPayload)),
        this.store$.pipe(select(selectFilesMetadata)),
        this.store$.pipe(select(selectLetterFilesMetadata)),
        this.store$.pipe(select(selectTestMonkeyState)),
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(selectAcceptedDAOffer)),
        this.store$.pipe(select(fromRootStore.selectIsCO0Flow)),
        this.store$.pipe(select(selectCO0UploadToggle)),
        this.store$.pipe(select(selectOAFromStore)),
        this.store$.pipe(select(selectIsOpeningNewAccountAutomatically)),
        this.store$.pipe(select(selectFraudDetectorSimulatorLevel)),
      ),
      filter(([, isSubmitInProgress, , , , , , , , , , ,]) => {
        return !isSubmitInProgress;
      }),
      tap(() => {
        this.store$.dispatch(submitInProgress());
      }),
      switchMap(
        ([
          ,
          ,
          submitPayload,
          filesMetadata,
          letterFilesMetadata,
          testMonkeyState,
          mockRecaptchaLevel,
          acceptedDAOffer,
          isCO0Flow,
          isCO0UploadToggle,
          oaFromStore,
          isAccountOpeningAutomatic,
          mockFraudDetectorLevel,
        ]) => {
          return forkJoin([
            of(submitPayload),
            this.convertToBase64PDF([...filesMetadata, ...letterFilesMetadata]),
            of(testMonkeyState),
            this.fmfService.getAllTokens({ recaptcha: 'COO_APPIAN_SUBMIT' }),
            isCO0Flow && isAccountOpeningAutomatic
              ? this.fmfService.getAllTokens({ recaptcha: 'COO_OA_SUBMIT' })
              : of(null),
            of(mockRecaptchaLevel),
            of(acceptedDAOffer),
            of(isCO0Flow),
            of(isCO0UploadToggle),
            isCO0UploadToggle
              ? from(
                  this.documentUploadService.uploadDocuments([
                    ...filesMetadata,
                    ...letterFilesMetadata,
                  ]),
                )
              : of(null),
            of(oaFromStore),
            isCO0Flow && isAccountOpeningAutomatic ? of(mockFraudDetectorLevel) : of(null),
          ]);
        },
      ),
      switchMap(
        ([
          submitPayload,
          base64Pdf,
          testMonkeyState,
          fmfResponse,
          oaFMFresponse,
          mockRecaptchaLevel,
          acceptedDAOffer,
          isCO0Flow,
          isCO0UploadToggle,
          documentFileName,
          oaFromStore,
          mockFraudDetectorLevel,
        ]) => {
          if (!isCO0UploadToggle) {
            submitPayload.config.base64Pdf = base64Pdf;
          }
          this.loggerService.info(
            { eventId: 'createEffects-status', message: 'Submit Payload in-Progress' },
            LoggerCodes.GENERAL_SUCCESS,
          );

          if (isCO0UploadToggle && documentFileName?.fileName) {
            submitPayload.config.caseInformationFiles = [
              {
                filename: documentFileName.fileName.split('.')[0],
                fileExtension: documentFileName.fileName.split('.')[1],
              },
            ];
          }

          const submitEndpoint = isCO0Flow
            ? this.submitService.submitMultiTransactionsEndpoint(
                submitPayload,
                fmfResponse,
                oaFMFresponse,
                mockRecaptchaLevel,
                oaFromStore,
                mockFraudDetectorLevel,
              )
            : this.submitService.submitEndpoint(submitPayload, fmfResponse, mockRecaptchaLevel);

          return submitEndpoint.pipe(
            switchMap((res) => {
              const accountId: string =
                submitPayload.config.selectedAccs.transferTo?.selectedAccount?.accountData
                  .accountId;

              const isMinorFlow: boolean = submitPayload.transactionType === 'MINOR';

              try {
                const transactionId =
                  res.appianSuccess?.bpmCaseId?.toString().replace(/,/g, '|') ||
                  res.transactId ||
                  '';
                const isStp = 'transactId' in res;

                const actions = [];
                if (isMinorFlow && acceptedDAOffer) {
                  actions.push(UpdateAccountId({ payload: accountId }));
                  actions.push(enrollInDA());
                }
                actions.push(
                  LogInfo({
                    payload: {
                      eventId: 'submitEffects-status',
                      message: 'Submit Payload Success',
                    },
                  }),
                );
                if (submitPayload.config.base64Pdf) actions.push(TrackSendDocuments());
                actions.push(
                  TrackProcessComplete({
                    processType: `coo-${flowMapper[submitPayload.config.triage.flowType]}-flow`,
                    accountType: submitPayload.config.triage.accountTypeInheriting,
                    transactionId,
                  }),
                );

                if (testMonkeyState?.monkeyMood) {
                  const monkeyTable = [res.appianSuccess?.bpmCaseId, res.success];
                  actions.push(testMonkeyUpdateResponses({ payload: monkeyTable }));
                }

                actions.push(submitMetricsFlowSubmit({ payload: transactionId }));
                actions.push(submitCompleted());
                actions.push(updateIsStp({ payload: isStp }));
                return actions;
              } catch (error) {
                return [
                  LogError({
                    payload: {
                      eventId: 'submitEndpoint-response-error',
                      message: `Submit Response Error: ${JSON.stringify(sanitizeError(error))}`,
                      code: LoggerCodes.NULL_FAILURE,
                    },
                  }),
                  submitCompleted(),
                ];
              }
            }),
            catchError((e) => {
              return [
                submitFailed(),
                closeSpinner(),
                LogError({
                  payload: {
                    eventId: 'error-page-catch-submitService',
                    message: `Submit Service Error: ${JSON.stringify(sanitizeError(e))}`,
                    code: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ];
            }),
          );
        },
      ),
      catchError((e) => {
        return [
          submitFailed(),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-catch-convertPDF',
              message: `Convert PDF Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    ),
  );

  internalTransferSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(internalTransferAppianSubmit),
      withLatestFrom(
        this.store$.pipe(select(selectIsSubmitInProgress)),
        this.store$.pipe(select(getITSubmitPayload)),
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(fromRootStore.selectIsCO0Flow)),
        this.store$.pipe(select(selectOAFromStore)),
        this.store$.pipe(select(selectIsOpeningNewAccountAutomatically)),
        this.store$.pipe(select(selectFraudDetectorSimulatorLevel)),
      ),
      filter(([, isSubmitInProgress, , , , ,]) => {
        return !isSubmitInProgress;
      }),
      tap(() => {
        this.store$.dispatch(submitInProgress());
      }),
      switchMap(
        ([
          ,
          ,
          submitPayload,
          mockRecaptchaLevel,
          isCO0Flow,
          oaFromStore,
          isAccountOpeningAutomatic,
          mockFraudDetectorLevel,
        ]) => {
          return forkJoin([
            of(removeRestrictedHoldings(submitPayload)),
            this.fmfService.getAllTokens({ recaptcha: 'COO_APPIAN_SUBMIT' }),
            isCO0Flow && isAccountOpeningAutomatic
              ? this.fmfService.getAllTokens({ recaptcha: 'COO_OA_SUBMIT' })
              : of(null),
            of(mockRecaptchaLevel),
            of(isCO0Flow),
            of(oaFromStore),
            isCO0Flow && isAccountOpeningAutomatic ? of(mockFraudDetectorLevel) : of(null),
          ]);
        },
      ),
      switchMap(
        ([
          submitPayload,
          fmfResponse,
          oaFMFResponse,
          mockRecaptchaLevel,
          isCO0Flow,
          oaFromStore,
          mockFraudDetectorLevel,
        ]) => {
          this.loggerService.info(
            {
              eventId: 'submit-effects-status',
              message: `Internal Transfer Submit in Progress for: ${submitPayload.itConfig.internalTransfer.transferFrom.totalTransferValue}`,
            },
            LoggerCodes.GENERAL_SUCCESS,
          );
          const submitEndpoint = isCO0Flow
            ? this.submitService.submitMultiTransactionsEndpoint(
                submitPayload,
                fmfResponse,
                oaFMFResponse,
                mockRecaptchaLevel,
                oaFromStore,
                mockFraudDetectorLevel,
              )
            : this.submitService.internalTransferSubmitEndpoint(
                submitPayload,
                fmfResponse,
                mockRecaptchaLevel,
              );

          return submitEndpoint.pipe(
            switchMap((res) => {
              const isTransactIDTypeSTP = 'transactId' in res;
              const isTransactIDTypeCWP =
                'appianSuccess' in res && 'bpmCaseId' in res.appianSuccess;

              let transactionId: string = '';
              if (isTransactIDTypeSTP) {
                transactionId = res.transactId;
              } else if (isTransactIDTypeCWP) {
                transactionId = (res.appianSuccess.bpmCaseId ?? [])
                  .map((item) => item.replace(/,/g, '|'))
                  .join(',');
              }

              const actions = [];
              actions.push(
                LogInfo({
                  payload: {
                    eventId: 'submitEffects-status',
                    message: 'Internal Transfer Submit Payload Success',
                  },
                }),
              );
              const transferFromAcctType =
                submitPayload.itConfig.internalTransfer.triage.transferFrom;
              const transferToAcctType = submitPayload.itConfig.internalTransfer.triage.transferTo;
              actions.push(
                TrackProcessComplete({
                  processType: `coo-internal_transfer-flow`,
                  accountType: `${transferFromAcctType}|${transferToAcctType}`,
                  transactionId,
                }),
              );
              actions.push(submitMetricsFlowSubmit({ payload: transactionId }));
              actions.push(submitCompleted());
              actions.push(updateIsStp({ payload: isTransactIDTypeSTP }));

              return actions;
            }),
            catchError((e) => {
              return [
                submitFailed(),
                closeSpinner(),
                LogError({
                  payload: {
                    eventId: 'error-page-catch-internal-transfer-submitService',
                    message: `Submit Service Error:${JSON.stringify(sanitizeError(e))}`,
                    code: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ];
            }),
          );
        },
      ),
      catchError((e) => {
        return [
          submitFailed(),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-catch-internal-transfer-submit-effects',
              message: `Submit Effects Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    ),
  );

  async convertToBase64PDF(filesMetaData: IFileMetaData[]): Promise<string> {
    try {
      if (filesMetaData.length > 0) {
        const pdfDoc = await PDFDocument.create();
        for (const file of filesMetaData) {
          const response = await this.windowRef.nativeWindow.fetch(file.objectURL);
          const contentType = response.headers.get('Content-Type');
          const byteArray = await response.arrayBuffer();

          if (contentType === 'application/pdf') {
            const PDFObject = await PDFDocument.load(byteArray);
            const totalPagesCount = PDFObject.getPageCount();
            const pageArray = [];

            for (let i = 0; i < totalPagesCount; i++) {
              pageArray.push(i);
            }

            const copiedPages = await pdfDoc.copyPages(PDFObject, pageArray);

            if (copiedPages.length > 0) {
              copiedPages.forEach((page) => {
                pdfDoc.addPage(page);
              });
            }
          } else if (contentType === 'image/jpeg' || contentType === 'image/jpg') {
            const jpegImage = await pdfDoc.embedJpg(byteArray);
            const page = pdfDoc.addPage();
            const resizedDim = jpegImage.scaleToFit(page.getWidth(), page.getHeight());
            page.drawImage(jpegImage, {
              x: 0,
              y: 0,
              width: resizedDim.width,
              height: resizedDim.height,
            });

            this.loggerService.info(
              { eventId: 'convertingJPG/JPEG-status', message: 'JPG/JPEG conversion' },
              LoggerCodes.GENERAL_SUCCESS,
            );
          } else if (contentType === 'image/png') {
            const pngImage = await pdfDoc.embedPng(byteArray);
            const page = pdfDoc.addPage();
            const resizedDim = pngImage.scaleToFit(page.getWidth(), page.getHeight());
            page.drawImage(pngImage, {
              x: 0,
              y: 0,
              width: resizedDim.width,
              height: resizedDim.height,
            });

            this.loggerService.info(
              { eventId: 'convertingPNG-status', message: 'PNG conversion' },
              LoggerCodes.GENERAL_SUCCESS,
            );
          }
        }

        const pdfBase64 = await pdfDoc.saveAsBase64();

        this.loggerService.info(
          { eventId: 'submitEffects-Success', message: 'PDF Conversion Success' },
          LoggerCodes.GENERAL_SUCCESS,
        );
        return pdfBase64;
      } else {
        this.loggerService.info(
          { eventId: 'convertToBase64PDF-status', message: 'PDF Conversion Skipped' },
          LoggerCodes.GENERAL_SUCCESS,
        );
        return '';
      }
    } catch (error) {
      this.loggerService.error(
        { eventId: 'convertToBase64PDF-error', message: String(error) },
        LoggerCodes.CONVERT_TO_PDF_FAILURE,
      );
      return '';
    }
  }

  sendEmail(
    prospect: boolean,
    triageState: TriageState,
    personalInfo: IPersonalInfoClientState['clientData'],
    clientEmailAddress: string,
  ): void {
    const isExecutor =
      triageState.individualDesignation === IndividualDesignationListedTypes.EXECUTOR;
    const isMinor = getIsMinorFlow(triageState);
    const isInternalTransfer = getIsInternalTransferFlow(triageState);
    const isGifting = getIsGiftingFlow(triageState);
    const clientName = getClientName(personalInfo);

    const emailAddress = prospect ? personalInfo.email : clientEmailAddress;

    let template;
    switch (true) {
      case isMinor:
        template = EmailTemplatesEnum.COO_UGMA_UTMA;
        break;
      case isGifting:
        template = EmailTemplatesEnum.COO_GIFTING_CONFIRM;
        break;
      case isExecutor:
        template = EmailTemplatesEnum.COO_EXECUTOR_CONFIRM;
        break;
      case isInternalTransfer:
        template = EmailTemplatesEnum.COO_INTERNAL_TRANSFER_CONFIRM;
        break;
      default:
        template = EmailTemplatesEnum.COO_BENEFICIARY_CONFIRM;
        break;
    }

    this.emailService
      .sendEmail({
        toAddress: emailAddress,
        template,
        clientName,
      })
      .subscribe();
  }

  submitCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitCompleted),
      withLatestFrom(
        this.store$.pipe(select(selectIsProspect)),
        this.store$.pipe(select(getOpenAccountState)),
        this.store$.pipe(select(selectTriageState)),
        this.store$.pipe(select(selectClientPersonalInfoData)),
        this.store$.pipe(select(selectClientEmailAddress)),
        this.store$.pipe(select(selectTestMonkeyState)),
        this.store$.pipe(select(fromRootStore.selectIsCO0Flow)),
      ),
      switchMap(
        ([
          ,
          prospect,
          openAccountState,
          triageState,
          personalInfo,
          clientEmailAddress,
          testMonkey,
          isCO0Flow,
        ]) => {
          const isIRAExecutor = getIsIRASingleExecutor(triageState);

          if (!isIRAExecutor && !isCO0Flow) {
            this.sendEmail(prospect, triageState, personalInfo, clientEmailAddress);
          }

          if (testMonkey?.monkeyMood) {
            return [testMonkeyResetState(), testMonkeyNextScenario()];
          } else {
            return prospect &&
              openAccountState.openAccountResponse &&
              openAccountState.openAccountResponse.success &&
              !openAccountState.openAccountResponse.isS24Reject
              ? [convertRegistration()]
              : [Next()];
          }
        },
      ),
      catchError((error) => {
        this.loggerService.error(
          {
            message: `Failed to retrieve open account response or isProspect flag with error: ${error}`,
          },
          LoggerCodes.NULL_FAILURE,
        );
        return [Next()];
      }),
    ),
  );

  submitError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitFailed),
      switchMap(() => {
        return [
          Go({ path: ['error'] }),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-route-submit',
              message: 'CWP Submit Failure',
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
          TrackProcessError({
            errorCode: 'coo_appian_error',
          }),
        ];
      }),
    ),
  );
}
